import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../utils/axios";
import { API_ROUTES } from "./routes";
import { showToast } from "../components/ui/toast";

// Get all products
export const useGetProductsQuery = (query = {}) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_PRODUCTS, query],
    queryFn: () => api.get(API_ROUTES.GET_PRODUCTS, { params: query }),
  });

  return {
    data: data?.data?.data ?? [],
    isPending,
    isError,
    error,
  };
};

// Get a single product
export const useGetProductByIdQuery = (id) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_PRODUCT, id],
    queryFn: () => api.get(`${API_ROUTES.GET_PRODUCT}/${id}`),
    enabled: !!id,
  });

  return {
    data: data?.data?.data ?? null,
    isPending,
    isError,
    error,
  };
};

// Create a product
export const useCreateProductMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (payload) =>
        api.post(API_ROUTES.CREATE_PRODUCT, payload, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }),
      onSuccess: (data, variables) => {
        const productType = variables.type;
        showToast(
          "success",
          `${productType === "subscription" ? "Subscription" : "Transactional"
          } product created successfully`
        );
      },
      onError: (err) => {
        showToast(
          "error",
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            "Failed to create product"
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries([API_ROUTES.GET_PRODUCTS]);
      },
    });

  return { mutate, mutateAsync, isError, error, isLoading, isSuccess, isPending };
};

// Update a product
export const useUpdateProductMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (payload) =>
        api.put(`${API_ROUTES.UPDATE_PRODUCT}/${payload?._id || payload?.get("_id")}`, payload, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }),
      onSuccess: (data, variables) => {
        const productType = variables.type;
        showToast(
          "success",
          `${productType === "subscription" ? "Subscription" : "Transactional"
          } product updated successfully`
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries([API_ROUTES.GET_PRODUCTS]);
      },
    });

  return { mutate, isError, error, isLoading, isSuccess, isPending };
};

// Delete a product
export const useDeleteProductMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (id) => api.delete(`${API_ROUTES.DELETE_PRODUCT}/${id}`),
      onSuccess: () => {
        showToast("success", "Product deleted successfully");
      },
      onSettled: () => {
        queryClient.invalidateQueries([API_ROUTES.GET_PRODUCTS]);
      },
    });

  return { mutate, isError, error, isLoading, isSuccess, isPending };
};
